import { CloseOutlineIcon, FullScreenOutlineIcon } from '@carboncopyco/icons';
import {
  Dialog,
  TextBase,
  TextBoxEditorTitle,
  TextLedeOpening,
  notify,
} from '@carboncopyco/ui';
import { getClientBoundaries } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { AnimatePresence, motion } from 'framer-motion';
import { orderBy } from 'lodash-es';
import type React from 'react';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { ChartBuilder } from './ChartBuilder';
import { TemplatesList } from './TemplatesList';
import { initialState, reducer } from './reducer';
import Templates from './templates';
import type { ICustomTemplateChart } from './types';
import { getTemplateName, isTemplateValid, templateMapper } from './utils';

const IconContainer = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <div
    className="h-full flex justify-between items-center cursor-pointer prevent-dragging"
    onClick={onClick}
  >
    {children}
  </div>
);

const dialogRightMargin = 19;
const initialY = 9;
const dialogWidth = 740;

export interface CustomChartBuilderProps {
  onClose: () => void;
  chartTemplates: ICustomTemplateChart[];
  fetchChartTemplates: () => Promise<void>;
  createChartTemplate: (
    chartTemplate: ICustomTemplateChart
  ) => Promise<ICustomTemplateChart>;
  updateChartTemplate: (values: {
    chartId: string;
    payload: ICustomTemplateChart;
  }) => Promise<void>;
  deleteChartTemplate: (id: string) => Promise<void>;
}

export const CustomChartBuilder = ({
  onClose,
  chartTemplates,
  fetchChartTemplates,
  createChartTemplate,
  updateChartTemplate,
  deleteChartTemplate,
}: CustomChartBuilderProps) => {
  const { clientWidth } = getClientBoundaries();
  const [isFullscreen, setIsFullscreen] = useState<boolean | null>(null);
  const initialX = clientWidth - dialogWidth - dialogRightMargin;
  const posRef = useRef({ x: initialX, y: initialY });
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoadingTemplates, setIsLoadingTemplates] = useBoolean(true);
  const templatesList = useMemo(
    () =>
      orderBy(chartTemplates, [(t) => t.templateName.toLowerCase()], ['asc']),
    [chartTemplates]
  );

  useEffect(() => {
    if (isLoadingTemplates) {
      fetchChartTemplates()
        .catch(() => notify.error('Failed to load templates'))
        .finally(setIsLoadingTemplates.setFalse);
    }
  }, [fetchChartTemplates, isLoadingTemplates, setIsLoadingTemplates]);

  const [{ x, y }, setDialogPosition] = useState({
    x: initialX,
    y: initialY,
  });

  useEffect(() => {
    if (isFullscreen) {
      if (posRef?.current) {
        posRef.current = { x, y };
      }
      setDialogPosition({ y: initialY, x: 76 });
    } else if (isFullscreen === false) {
      if (posRef?.current) {
        setDialogPosition({ ...posRef.current });
      }
    }
  }, [isFullscreen]);

  async function handleSave(): Promise<void> {
    if (!isTemplateValid(state)) {
      return;
    }

    const template = JSON.stringify(
      templateMapper(state.template as Highcharts.Options)
    );

    const payload: ICustomTemplateChart = {
      isActive: state.isActive,
      template,
      templateIcon: state.templateIcon,
      templateName: state.templateName,
    };

    if (state.id) {
      return updateChartTemplate({
        chartId: state.id,
        payload: { ...payload },
      })
        .then(() => {
          notify.success(
            `Chart template "${state.templateName}" was successfuly saved with changes`
          );
        })
        .catch(() => {
          notify.error('Sorry, something went wrong');
        });
    }

    return createChartTemplate(payload)
      .then((data) => {
        dispatch({
          type: 'SET_TEMPLATE',
          payload: { id: data.id, ...state },
        });
        notify.success('New chart template created');
      })
      .catch(() => {
        notify.error('Sorry, something went wrong');
      });
  }

  async function handleDelete() {
    deleteChartTemplate(state.id)
      .then(() => {
        notify.success(`Chart template “${state.templateName}” was successfuly deleted.
            `);

        dispatch({
          type: 'SET_TEMPLATE',
          payload: {
            ...initialState,
          },
        });
      })
      .catch(() => {
        notify.error(`“${state.templateName}” chart template delete failed.`);
      });
  }

  return (
    <AnimatePresence>
      <Draggable
        data-edit-tool="true"
        handle=".handle-dragging"
        cancel=".prevent-dragging"
        position={{
          x,
          y,
        }}
        onStop={(_e, position) => {
          setDialogPosition({
            x: position.x,
            y: position.y,
          });
        }}
      >
        <div
          style={{
            zIndex: 999,
            position: 'absolute',
            height: 'calc(100% - 18px)',
            width: isFullscreen ? 'calc(100% - 89px)' : dialogWidth,
          }}
        >
          <motion.div
            key="dialog"
            transition={{ duration: 0.2, ease: 'easeIn' }}
            initial={{
              opacity: 0,
              height: '100%',
              width: '100%',
            }}
            animate={{
              opacity: 1,
            }}
            exit={{ opacity: 0 }}
          >
            <Dialog
              dialogStyle={{
                overflow: 'hidden',
                paddingBottom: 0,
              }}
              width="100%"
              height="100%"
              contentClassName="h-full w-full flex"
              header={
                <div className="pb-2.5 pt-1.5 flex items-center w-full justify-between cursor-move handle-dragging ">
                  <TextBoxEditorTitle
                    fontWeight="font-ds-medium"
                    label="Custom Chart Templates"
                    uppercase
                    color="text-ds-natural-600"
                  />
                  <div className="h-full flex items-center space-x-3.5 prevent-dragging">
                    <IconContainer
                      onClick={() => {
                        setIsFullscreen((prev: any) => Boolean(!prev));
                      }}
                    >
                      <FullScreenOutlineIcon size={20} />
                    </IconContainer>
                    <IconContainer onClick={() => onClose()}>
                      <CloseOutlineIcon size={20} />
                    </IconContainer>
                  </div>
                </div>
              }
              footer={<div />}
            >
              <div className="flex-1  w-full flex  border-t text-ds-natural-150 border-ds-natural-150 border-solid">
                <div className="border-r text-ds-natural-150 border-ds-natural-150 border-solid w-1/3 max-w-xs pt-3.5">
                  <TemplatesList
                    isLoadingTemplates={isLoadingTemplates}
                    onAddNewTemplateClick={() => {
                      dispatch({
                        type: 'SET_TEMPLATE',
                        payload: {
                          ...initialState,
                          templateName: getTemplateName({}, templatesList),
                          template: Templates.column,
                        },
                      });
                    }}
                    templates={templatesList}
                    selectedTemplate={state.id}
                    onTemplateClick={(selcetedTemplate) => {
                      dispatch({
                        type: 'SET_TEMPLATE',
                        payload: {
                          isActive: selcetedTemplate.isActive,
                          id: selcetedTemplate.id,
                          templateName: selcetedTemplate.templateName,
                          template: selcetedTemplate.template,
                          templateIcon: selcetedTemplate.templateIcon,
                        },
                      });
                    }}
                  />
                </div>
                <div
                  className="pt-3.5"
                  style={{
                    width: isFullscreen ? '100%' : 'calc(100% - 33%)',
                  }}
                >
                  {state.template !== null ? (
                    <AnimatePresence>
                      <motion.div
                        key="builder"
                        className="w-full h-full overflow-y-auto overflow-x-hidden"
                        initial={{
                          opacity: 0,
                          width: '100%',
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        exit={{ opacity: 0 }}
                      >
                        <ChartBuilder
                          onClose={onClose}
                          onDelete={handleDelete}
                          dispatch={dispatch}
                          state={state}
                          templates={templatesList}
                          onSubmit={handleSave}
                        />
                      </motion.div>
                    </AnimatePresence>
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center">
                      <TextLedeOpening
                        label="Start creating new template"
                        color="text-ds-natural-600"
                      />
                      <TextBase
                        label="Create and edit custom chart templates"
                        color="text-ds-natural-300"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Dialog>
          </motion.div>
        </div>
      </Draggable>
    </AnimatePresence>
  );
};
